import {
  adminSwitchboardVars,
  defaultSwitchboardVars,
  deleteSwitchboards,
  ecopetrolSwitchboardVars,
  kocSwitchboardVars,
  restoreSwitchboards
} from "@/models/equipment/switchboards/predicates";
import type { Switchboards } from "gql/api";
import {
  useDeleteSwitchboardsMutation,
  useInsertSwitchboardMutation,
  useRestoreSwitchboardsMutation,
  useSwitchboardsQuery,
  useUpdateSwitchboardByPkMutation
} from "gql/api";
import { v4 } from "uuid";
import { computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { switchboardTableColumns } from "./switchboardTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useSwitchboards(isAdmin: boolean): UseEquipment<Switchboards> {
  const baseEquipment = useEquipment<Switchboards>(isAdmin);
  const title = "Switchboards";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocSwitchboardVars;
    if (user.is_ecopetrol_customer) return ecopetrolSwitchboardVars;
    if (user.is_admin) return adminSwitchboardVars;
    return defaultSwitchboardVars;
  };

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useSwitchboardsQuery({
    variables: getVariables()
  });

  const allRows = computed(
    () => data.value?.switchboards as Switchboards[] | undefined
  );

  const hashOrder: (keyof Switchboards)[] = ["manufacturer", "part_number"];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertSwitchboard } =
    useInsertSwitchboardMutation();
  const { executeMutation: executeUpdateSwitchboard } =
    useUpdateSwitchboardByPkMutation();
  const { executeMutation: executeDeleteSwitchboard } =
    useDeleteSwitchboardsMutation();
  const { executeMutation: executeRestoreSwitchboard } =
    useRestoreSwitchboardsMutation();

  const insertSwitchboard = (
    switchboard: Omit<Switchboards, "sizing_import" | "user">
  ) => executeInsertSwitchboard({ object: switchboard });

  const updateSwitchboard = (id: string, switchboard: Switchboards) =>
    executeUpdateSwitchboard({ id, object: switchboard });

  const deleteSwitchboard = (ids: string[]) =>
    executeDeleteSwitchboard({ where: deleteSwitchboards(ids) });

  const restoreSwitchboard = (ids: string[]) =>
    executeRestoreSwitchboard({ where: restoreSwitchboards(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "switchboards",
    id: v4(),
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    imported_sizing_id: null,
    manufacturer: "",
    part_number: "",
    enclosure: "",
    amps: null,
    kv: null
  }));

  return {
    searchPlaceholder: "Search Switchboards...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "3.6"`,
    columns: defineVisibleColumns(switchboardTableColumns),

    handleUpsert: defineUpsert(insertSwitchboard, updateSwitchboard),
    handleDelete: defineDelete(deleteSwitchboard),
    handleRestore: defineRestore(restoreSwitchboard),
    totalCount: computed(
      () => data.value?.switchboards_aggregate.aggregate?.count
    ),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: switchboardTableColumns,
      globalSearchFields: ["enclosure", "part_number", "kv"],
      allRows,
      title
    })
  };
}
